import { SUBMIT_DOC_FLOW, validateSubmitDocuments } from '../submitDocumentValidation';
import { getNextStepV2, V2_TYPE } from './helperV2';

// Post-submit landing route per V2 application type. Navigating here (without the
// `?step=` wizard query) lets the app re-resolve to the Thank-You / ask-more-questions /
// additional-documents page from the freshly fetched application state.
export const getUnifiedBusinessAppUrl = (type, editId) => {
    if (Number(type) === V2_TYPE.PRIME) {
        return `/business-prime-plus/${editId}`;
    }
    if (Number(type) === V2_TYPE.UPGRADE) {
        return `/business-prime/${editId}`;
    }
    return `/business-plus/${editId}`;
};

// Shared final-submission flow for Business V2 onboarding, used by BOTH the payment step
// (types 1 & 2) and the Terms & Conditions step (type 3, which has no payment step):
//   1. validate that every required document is uploaded,
//   2. run the final submit (handleUpdate with isLastStep),
//   3. redirect to the post-submit page (Thank-You / ask-more-questions / additional-docs).
// Centralised so the two entry points can never drift apart.
//
// Provide `nextStep`, or `wizard` + `type` + `jsonConfig` for it to be derived.
//
// `skipUpdate` controls whether the step's own data is persisted via
// `/api/business/update` before the submit. The payment step keeps the default
// (true) because the step before it already saved; the Terms & Conditions step
// (type 3, where it is the LAST step) must pass false so its data is saved first.
export const finalizeBusinessV2Submission = async ({
    editId,
    data,
    applicationForm,
    nextStep,
    type,
    wizard,
    jsonConfig,
    skipUpdate = true,
    handleUpdate,
    setHasError,
    showError,
    navigate,
}) => {
    try {
        const documentValidation = await validateSubmitDocuments({
            flow: SUBMIT_DOC_FLOW.BUSINESS_V2_ONBOARDING,
            recordId: editId,
            data,
            applicationForm,
            formType: applicationForm?.formType,
        });

        if (!documentValidation?.isValid) {
            setHasError?.(true);
            showError?.(documentValidation?.errors?.[0]?.error || "Please upload all required documents before submitting.");
            return { status: 400 };
        }

        const result = await handleUpdate({
            nextStep: nextStep ?? getNextStepV2(wizard, type, data, jsonConfig),
            isfetchDetail: true,
            isLastStep: true,
            skipUpdate,
        });

        if (result?.status === 200 && typeof navigate === 'function') {
            navigate(getUnifiedBusinessAppUrl(applicationForm?.type ?? type, editId), { replace: true });
        }

        return result;
    } catch (error) {
        setHasError?.(true);
        showError?.(error);
        return { status: 400 };
    }
};
